import React, { useEffect, useState } from "react";
import { ButtonBase, BadgeBase, ModalBase } from "@reusejs/react";
import { amountFormatter, shortlistProperty } from "services";
import ImagePreview from "./ImagePreview";
import {
  StarIcon,
  ShareIcon,
  PrinterIcon,
  XIcon,
  ExclamationCircleIcon,
} from "@heroicons/react/outline";
import { CheckCircleIcon } from "@heroicons/react/solid";
import CallRequestModal from "./CallRequestModal";
import { DateTime } from "luxon";
import { useSelector } from "react-redux";
import LoginModalComponent from "./LoginModal";
import InterestModalComponent from "./InterestModal";
import { useRouter } from "next/router";
import CrossSvg from "../../Icons/CrossSvg";
import InterestForm from "./InterestForm";
import Tooltip from "./Tooltip";
import ShareBtnSvg from "../../Icons/ShareBtnSvg";

function PropertyDetail(props: any) {
  const router = useRouter();
  const [success, setSuccess] = useState(false);
  const [showCallRequest, setShowCallRequest] = useState(false);
  const [showDepositDeadlineAlert, setShowDepositDeadlineAlert] =
    useState(true);
  const [showCopiedPopup, setShowCopiedPopup] = useState(false);
  const [interestedCount, setInterestedCount] = useState<number>(0);

  const userToken = useSelector((state: any) => {
    return state.auth.accessToken;
  });

  const userUuid = useSelector((state: any) => {
    return state.auth.user?.uuid;
  });

  const shortlistedProperties = useSelector((state: any) => {
    return state.auth?.user?.profile?.meta?.shortlisted_properties;
  });

  useEffect(() => {
    setTimeout(function () {
      setShowCallRequest(true);
    }, 5000);
  }, []);

  useEffect(() => {
    if (props.property.interested_count) {
      setInterestedCount(props.property.interested_count);
    }
  }, [props.property.interested_count]);

  const handleClose = () => {
    setShowCallRequest(false);
  };

  const LoginModalWrapper = (props: any) => {
    return (
      <LoginModalComponent
        heading="Login to shortlist"
        subHeading="Please login or create account to shortlist."
        description="Property shortlist is only available to Auction bazaar users."
        router={router}
        {...props}
      />
    );
  };

  const InterestModalWrapper = (propsA: any) => {
    return (
      <InterestModalComponent
        heading="Thanks for your interest"
        subHeading="Please select a date and time for a detail call with our property executives"
        description={
          <InterestForm
            {...propsA}
            property_uuid={props.property.uuid}
            listing_id={props.property.listing_id}
            user_uuid={userUuid ? userUuid : ""}
            successCallback={() => setSuccess(true)}
          />
        }
        router={router}
        {...propsA}
      />
    );
  };

  const openModal = async (modal: any) => {
    await ModalBase(
      {
        content: modal ? modal : LoginModalWrapper,
        timeout: 0,
        backgroundColor: "bg-black/30",
        backgroundOpacity: "opacity-70",
        modalBaseClasses: {
          background: "bg-white",
          border: "border-2 rounded-lg",
          alignment: "z-[999] overflow-visible",
          padding: "p-0",
          small: "max-w-4xl",
        },
      },
      1000,
      "__next"
    );
  };

  const handleShortListed = async (uuid: any) => {
    try {
      if (userToken) {
        const data: any = await shortlistProperty(uuid, interestedCount);
        setInterestedCount(data.interested_count);
      } else {
        openModal(LoginModalWrapper);
      }
    } catch (error) {
      console.log("check error", error);
    }
  };

  const current_date_object = DateTime.now().setZone("Asia/Kolkata").set({
    hour: 0,
    minute: 0,
    second: 0,
    millisecond: 0,
  });
  const emd_last_date_object = DateTime.fromISO(
    props.property.emd_last_date && props.property.emd_last_date
  ).setZone("Asia/Kolkata");
  const diffDays = Math.floor(
    emd_last_date_object.diff(current_date_object, "days").days
  );

  return (
    <div className="bg-white">
      <div className="relative mt-5 md:mt-4 py-0 md:py-8 lg:mx-auto lg:max-w-7xl">
        <div>
          <h2 className="text-2xl text-start md:text-3xl px-4 lg:px-[initial] md:text-center md:text-left font-extrabold tracking-tight sm:text-4xl capitalize flex items-center">
            Listing ID: {props.property.listing_id}
            <span className="lg:hidden flex ml-4">
              <ButtonBase
                type="submit"
                label={<ShareBtnSvg />}
                onClick={() => {
                  setShowCopiedPopup(true);
                  setTimeout(() => {
                    setShowCopiedPopup(false);
                  }, 2000);
                  window.navigator.clipboard.writeText(window.location.href);
                }}
                buttonBaseClasses={{
                  backgroundColor: "bg-transparent",
                  textColor: "text-black hover:text-blue-700",
                  padding: "0",
                  // border: "border border-gray-300 hover:border-blue-700",
                }}
              />
            </span>
          </h2>
          <div className="mt-4 flex px-4 lg:px-0 justify-start">
            <BadgeBase
              label={props.property?.asset?.name}
              badgeBaseClasses={{
                padding: "px-3 py-0.5 mr-4 md:mr-2",
                backgroundColor: "bg-red-100",
                textColor: "text-red-700 capitalize",
                font: "text-xs md:text-sm font-medium",
                borderRadius: "rounded-md",
              }}
            />
            <BadgeBase
              label={props?.property?.sub_asset_type?.name}
              badgeBaseClasses={{
                padding: "px-3 py-0.5",
                backgroundColor: "bg-blue-100",
                textColor: "text-blue-700 capitalize",
                font: "text-xs md:text-sm font-medium",
                borderRadius: "rounded-md",
              }}
            />
          </div>
        </div>
        <div className="lg:grid lg:grid-cols-2 lg:gap-20 lg:items-start">
          <ImagePreview
            property_images={props.property.images}
            interestedCount={interestedCount}
            viewCount={props.viewCount}
            property={props.property}
          />

          <div className="relative max-w-md px-4 sm:max-w-3xl sm:px-6 lg:px-0">
            <div className="pt-0 sm:pt-16 border-b pb-4 lg:pt-16">
              <div className="px-0 lg:px-4">
                <div className="flex items-center text-base md:text-sm text-gray-500">
                  Reserve Price
                  <div className="ml-1">
                    <Tooltip
                      text="The lowest acceptable price specified by the seller for an item sold at auction."
                      wrapperClass="w-64 whitespace-wrap -top-24 p-3"
                    >
                      <ExclamationCircleIcon className="h-4 w-4 hover:text-blue-600" />
                    </Tooltip>
                  </div>
                </div>
                <div className="text-3xl leading-10 md:leading-[initial] md:text-2xl font-bold text-blue-600">
                  {props.property.property_date_type === "undated" ? (
                    <div
                      className="cursor-pointer hover:text-blue-700"
                      onClick={() => {
                        const baseurl = window.location.href
                          .split("/")
                          .filter(Boolean)[1];
                        const link =
                          "https://" +
                          baseurl +
                          "/auction-properties/" +
                          props.property.listing_id;

                        window.open(
                          `https://wa.me/918370969696?text=${encodeURI(
                            `Hello, I would like to know the price of this property: ${link}`
                          )}`,
                          "_blank"
                        );
                      }}
                    >
                      Contact Us
                    </div>
                  ) : (
                    `₹${amountFormatter(props.property.reserve_price)}`
                  )}
                </div>
              </div>
              <div className="text-left mt-6 pt-6 border-t px-0 lg:px-4 flex justify-center sm:justify-between">
                <div className="flex basis-1/2 py-4 flex-col">
                  <div className="text-xxs md:text-xs text-gray-500">
                    Bidding Start Date and Time
                  </div>
                  <div className="text-sm md:text-base">
                    <div className="flex items-center divide-x divide-gray-400">
                      <div className="pr-3">
                        {props.property.property_date_type === "undated" ? (
                          <span className="text-red-600">Coming Soon</span>
                        ) : props.property.property_date_type ===
                          "private_treaty" ? (
                          <span className="text-green-600">Private Treaty</span>
                        ) : (
                          props.property.auction_start_date_time &&
                          DateTime.fromISO(
                            props.property.auction_start_date_time
                          )
                            .setZone("Asia/Kolkata")
                            .toFormat("dd MMMM yyyy")
                        )}
                      </div>
                      <div className="px-3 text-gray-400 text-xxs md:text-xs">
                        {props.property.property_date_type === "dated" &&
                          props.property.auction_start_date_time &&
                          `${DateTime.fromISO(
                            props.property.auction_start_date_time
                          )
                            .setZone("Asia/Kolkata")
                            .toFormat("hh:mm a")} (IST)`}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="flex basis-1/2 py-4 flex-col sm:pl-6">
                  <div className="text-xxs md:text-xs text-gray-500 ">
                    Bidding Closure Date and Time
                  </div>
                  <div className="text-sm md:text-base">
                    <div className="flex items-center divide-x divide-gray-400">
                      <div className="pr-3">
                        {props.property.property_date_type === "undated" ? (
                          <span className="text-red-600">Coming Soon</span>
                        ) : props.property.property_date_type ===
                          "private_treaty" ? (
                          <span className="text-green-600">Private Treaty</span>
                        ) : (
                          props.property.auction_end_date_time &&
                          DateTime.fromISO(props.property.auction_end_date_time)
                            .setZone("Asia/Kolkata")
                            .toFormat("dd MMMM yyyy")
                        )}
                      </div>
                      <div className="px-3 text-gray-400 text-xxs md:text-xs">
                        {props.property.property_date_type === "dated" &&
                          props.property.auction_end_date_time &&
                          `${DateTime.fromISO(
                            props.property.auction_end_date_time
                          )
                            .setZone("Asia/Kolkata")
                            .toFormat("hh:mm a")} (IST)`}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="text-left px-0 lg:px-4 flex justify-center sm:justify-between">
                <div className="flex basis-1/2 py-4 flex-col">
                  <div className="text-xxs md:text-xs text-gray-500">
                    Auction Date
                  </div>
                  <div className="text-sm md:text-base">
                    {props.property.property_date_type === "undated" ? (
                      <span className="text-red-600">Coming Soon</span>
                    ) : props.property.property_date_type ===
                      "private_treaty" ? (
                      <span className="text-green-600">Private Treaty</span>
                    ) : (
                      props.property.auction_start_date_time &&
                      DateTime.fromISO(props.property.auction_start_date_time)
                        .setZone("Asia/Kolkata")
                        .toFormat("dd MMMM yyyy")
                    )}
                  </div>
                </div>
                <div className="flex basis-1/2 py-4 flex-col sm:pl-6">
                  <div className="text-xxs md:text-xs text-gray-500">
                    EMD Amount
                  </div>
                  <div className="text-sm md:text-base">
                    {props.property.property_date_type === "undated" ? (
                      <span className="text-red-600">Coming Soon</span>
                    ) : props.property.property_date_type ===
                      "private_treaty" ? (
                      <span className="text-green-600">Private Treaty</span>
                    ) : (
                      `₹${amountFormatter(props.property?.emd_amount)}`
                    )}
                  </div>
                </div>
              </div>
              {props.property.property_date_type === "dated" && (
                <div className="mt-2 sm:mx-4 p-3 rounded-lg text-sm bg-gray-50 text-blue-700">
                  EMD Date deadline:{" "}
                  {props.property.emd_last_date &&
                    DateTime.fromISO(props.property.emd_last_date)
                      .setZone("Asia/Kolkata")
                      .toFormat("dd MMMM yyyy")}
                </div>
              )}

              {props.property.property_date_type === "dated" &&
                showDepositDeadlineAlert && (
                  <div className="sm:mx-4 p-3 mt-4 rounded-lg text-sm bg-red-50 text-red-700 flex justify-between">
                    {diffDays > 0 ? (
                      <div>
                        EMD deposit ends in{" "}
                        <span className="font-bold">{diffDays} Days !</span>
                      </div>
                    ) : diffDays === 0 ? (
                      <div>
                        EMD deposit ends{" "}
                        <span className="font-bold">Today !</span>
                      </div>
                    ) : (
                      <div>EMD deposit deadline has ended.</div>
                    )}
                    <div
                      className="cursor-pointer flex items-center justify-center"
                      onClick={() => {
                        setShowDepositDeadlineAlert(false);
                      }}
                    >
                      <CrossSvg />
                    </div>
                  </div>
                )}
            </div>
            <div className="flex flex-col lg:flex-row mt-4 sm:px-4 items-center justify-between">
              <div className="mt-2 lg:mt-0 lg:mr-2 w-full">
                <ButtonBase
                  variant="primary"
                  type="submit"
                  label="Know More" //"Start Bidding"
                  onClick={() => {
                    openModal(InterestModalWrapper);
                  }}
                  buttonBaseClasses={{
                    font: "text-sm font-medium",
                    backgroundColor: "bg-blue-600 hover:bg-blue-700",
                    textColor: "text-white",
                    border: "any",
                    width: "w-full",
                    padding: "px-4 py-3",
                  }}
                />
              </div>
              <div className="mt-5 mb-6 lg:mb-0  lg:mt-0 lg:mt-0 w-full ">
                <ButtonBase
                  variant="primary"
                  type="submit"
                  label={
                    shortlistedProperties &&
                    shortlistedProperties.includes(props.property.uuid)
                      ? "Remove from shortlist"
                      : "Shortlist"
                  }
                  buttonPrefix={
                    <StarIcon
                      width="16"
                      height="16"
                      fill={
                        shortlistedProperties &&
                        shortlistedProperties.includes(props.property.uuid)
                          ? "currentcolor"
                          : "none"
                      }
                      className="mr-2"
                    />
                  }
                  onClick={() => handleShortListed(props.property.uuid)}
                  buttonBaseClasses={{
                    font: "text-sm font-medium",
                    backgroundColor: "bg-transparent",
                    textColor:
                      shortlistedProperties &&
                      shortlistedProperties.includes(props.property.uuid)
                        ? "text-blue-700 hover:text-black"
                        : "text-black hover:text-blue-700",
                    width: "w-full",
                    border:
                      shortlistedProperties &&
                      shortlistedProperties.includes(props.property.uuid)
                        ? "border border-blue-700 border hover:border-black"
                        : "border border-black border hover:border-blue-700",
                    padding: "px-4 py-3",
                  }}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="mt-4">
          <div className="lg:grid lg:grid-cols-2 lg:gap-20 lg:items-start">
            <div className="flex justify-center sm:justify-start">
              {showCopiedPopup && (
                <div
                  className={
                    "absolute z-50 bottom-2 mt-2 left-1/2 -translate-x-1/2 h-fit w-fit py-2 px-8 border border-white rounded-lg bg-black text-center text-white transition-all " +
                    (showCopiedPopup ? "opacity-75" : "opacity-0")
                  }
                >
                  Copied to Clipboard
                </div>
              )}
              <div className="hidden lg:block mr-2 flex-col">
                <ButtonBase
                  variant="primary"
                  type="submit"
                  label="Share Property"
                  buttonPrefix={
                    <ShareIcon width="16" height="16" className="mr-2" />
                  }
                  onClick={() => {
                    setShowCopiedPopup(true);
                    setTimeout(() => {
                      setShowCopiedPopup(false);
                    }, 2000);
                    window.navigator.clipboard.writeText(window.location.href);
                  }}
                  buttonBaseClasses={{
                    font: "text-sm font-medium",
                    backgroundColor: "bg-transparent",
                    textColor: "text-black hover:text-blue-700",
                    border: "border border-gray-300 hover:border-blue-700",
                  }}
                />
              </div>
              <div className="hidden lg:block">
                <ButtonBase
                  variant="primary"
                  type="submit"
                  label="Print"
                  buttonPrefix={
                    <PrinterIcon width="16" height="16" className="mr-2" />
                  }
                  onClick={() => {
                    userToken ? window.print() : window.alert("Please Log In");
                  }}
                  buttonBaseClasses={{
                    font: "text-sm font-medium",
                    backgroundColor: "bg-transparent",
                    textColor: "text-black hover:text-blue-700",
                    border: "border border-gray-300 hover:border-blue-700",
                  }}
                />
              </div>
            </div>
            {success && (
              <div className="w-full px-12">
                <div className="w-full p-3 rounded-lg text-sm bg-green-50 text-green-700 flex justify-between">
                  <div className="flex items-center">
                    <CheckCircleIcon height={16} width={16} className="mr-2" />
                    Thank you for your interest, we will reach out to you!
                  </div>
                  <XIcon
                    height={16}
                    width={16}
                    className="font-bold cursor-pointer"
                    onClick={() => {
                      setSuccess(false);
                    }}
                  />
                </div>
              </div>
            )}
          </div>
          {showCallRequest && (
            <div className="fixed z-10  inset-x-2 sm:inset-x-72 bottom-3">
              <CallRequestModal
                closeCallback={handleClose}
                listing_id={props.property.listing_id}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default PropertyDetail;
